/**
 * What the account is, in one strip — the way the terminal draws it.
 *
 * This region has now been wrong twice, in opposite directions. First it was a
 * single band with five unlike things spread across a twelve-column row. Then,
 * correcting that, it became a grid of bordered cards, each figure in its own
 * box with its own share and its own progress bar — which is a product
 * dashboard, and this is not one.
 *
 * The mock is a trading terminal, and its account strip is one panel, one row:
 * the equity large, the results beside it, then a vertical rule, then the
 * balances as plain label-over-value pairs at body size, then a rule, then the
 * slot count. Under a horizontal rule inside the same panel, the allocation bar
 * with its legend. No card carries a single number; the border is spent on the
 * panel, and inside it groups are separated by a hairline and by space.
 *
 * That shape is the point rather than a preference. The screen's subject is the
 * cycle table below, and a header that takes three hundred pixels to say what
 * the account holds pushes the subject under the fold. The mock's own header is
 * a sixth of the page: one figure to land on, six to read across, and then the
 * work.
 *
 * Equity is deliberately absent. It lives in the global bar, where the baseline
 * requires it to be readable on every screen, and one figure has one primary
 * place.
 *
 * Every figure here is point-in-time and carries no period control: a period
 * control governs everything inside its region, and a state true at a moment
 * must never sit under one.
 */
import { AllocationBar } from "@/design-system/AllocationBar";
import { Metric } from "@/design-system/Metric";
import { projectAccountAnalytics } from "@/shared/analytics/presentation";
import type { AccountAnalyticsResponse, CapitalResponse, OverviewResponse } from "@/shared/api";
import { isZero, signedMoney } from "@/shared/money/money";

type Props = {
  readonly overview: OverviewResponse;
  readonly capital: CapitalResponse | null;
  readonly today: AccountAnalyticsResponse | null;
  readonly loading: boolean;
};

/**
 * The share of equity the slot pools may not spend, when there is one.
 *
 * It is the MAJOR pool's allocation: a pool that holds a share of the account
 * rather than buying slots out of the same budget, so CORE and FAST divide what
 * is left after it. Read from the pool rather than subtracted here — a screen
 * that derives a balance is a screen that can disagree with the ledger.
 *
 * BASE v2.0 creates no MAJOR slot, so on this account the pool is a rule funded
 * with zero and the API keeps listing it. Nothing is where nothing is: a column
 * reading 0.00 reports a pool the account cannot put money into. It comes back
 * with the money, and only with the money.
 */
function reserved(capital: CapitalResponse | null): string | null {
  const pool = capital?.pools.find((one) => one.pool === "MAJOR") ?? null;
  if (pool === null || isZero(pool.allocated)) return null;
  return pool.allocated;
}

/** The account state as a mark on the figures it qualifies: live only while
 *  the figures are; otherwise said to be the last known (audit A11). */
function Live({ stale }: { readonly stale: boolean }): React.JSX.Element {
  if (stale) {
    return (
      <span
        data-testid="account-live"
        className="mb-1 rounded-row border border-status-warning px-1.5 font-numeric text-label-caps text-status-warning"
        title="Связь с сервером потеряна или данные устарели: показаны последние известные цифры"
      >
        НЕ ОБНОВЛЯЕТСЯ
      </span>
    );
  }
  return (
    <span
      data-testid="account-live"
      className="mb-1 rounded-row border border-status-info px-1.5 font-numeric text-label-caps text-status-info"
      title="Эти цифры верны на текущий момент и не меняются при переключении периода ниже"
    >
      LIVE
    </span>
  );
}

export function AccountSummary({
  overview,
  capital,
  today,
  loading,
}: Props): React.JSX.Element {
  const todayEvidence = today === null ? null : projectAccountAnalytics(today);
  const held = reserved(capital);
  const running = overview.active_cycles;
  // Every slot the three pools aim at, not what the balance funds today.
  const ceiling =
    capital === null ? null : capital.pools.reduce((sum, pool) => sum + pool.capacity, 0);

  return (
    <section
      aria-labelledby="account-summary-heading"
      data-overview-region="account"
      data-testid="account-summary"
      className="rounded-panel border border-border-subtle bg-surface-default p-3"
    >
      <h2 id="account-summary-heading" className="sr-only">
        Состояние счёта
      </h2>

      {/* One row, three groups. The values sit on a common bottom line, so the
          32px figure and the 14px ones read as one instrument rather than as
          three blocks at three heights. */}
      <div
        data-testid="account-strip"
        className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3"
      >
        <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
          <div className="flex items-end gap-2">
            <Metric
              metricId="account.active_capital"
              role="primary"
              size="display"
              value={overview.active_capital}
            />
            <Live stale={overview.stale} />
          </div>
          <Metric
            metricId="pnl.today_realised_net"
            role="primary"
            value={todayEvidence?.realisedNet ?? null}
            display={
              todayEvidence === null
                ? loading
                  ? "…"
                  : undefined
                : signedMoney(todayEvidence.realisedNet)
            }
            directional
          />
          <Metric
            metricId="pnl.unrealised"
            role="primary"
            value={overview.unrealised}
            directional
          />
        </div>

        {/* A hairline, not four boxes. These four are the same question asked
            about four places, and a border around each says they are four
            different kinds of thing. */}
        <div className="flex flex-wrap items-end gap-x-6 gap-y-3 sm:border-l sm:border-border-subtle sm:pl-6">
          <Metric
            metricId="account.strategy_free_capital"
            role="context"
            value={overview.strategy_free_capital}
          />
          <Metric
            metricId="account.position_margin"
            role="context"
            value={overview.position_margin}
          />
          <Metric
            metricId="account.reserved_grid_capital"
            role="context"
            value={overview.reserved_grid_capital}
          />
          <Metric
            metricId="account.withdrawable"
            role="context"
            value={overview.withdrawable}
          />
          {overview.withheld_profit != null && !isZero(overview.withheld_profit) && (
            <Metric metricId="account.withheld_profit" role="context" value={overview.withheld_profit} />
          )}
          {held !== null && (
            // No hint under it. A second line here is one group taller than
            // its neighbours, and on a row aligned to a common baseline that
            // lifts one value off the line the others share. What the pool is
            // for is in its name and in the contract, not in the strip.
            <Metric metricId="account.major_reserved" role="context" value={held} />
          )}
        </div>

        <div className="sm:border-l sm:border-border-subtle sm:pl-6">
          <Metric
            label="Слоты"
            testId="account-slots"
            // A count, not money — passed as the value so the figure is present
            // rather than absent. `display` alone reads as no measurement, and
            // the strip printed a dash beside nineteen running cycles.
            value={String(running)}
            display={
              ceiling === null ? String(running) : `${String(running)} / ${String(ceiling)}`
            }
          />
        </div>
      </div>

      {/* The same balance as a shape, under a rule inside the same panel: one
          fact drawn twice for two kinds of reading, not two panels. */}
      <div className="mt-3 border-t border-border-subtle pt-3">
        <AllocationBar capital={capital} overview={overview} />
      </div>
    </section>
  );
}
